/**
 * MyConnections — standalone page for accessing connections.
 *
 * Solves the "3 taps deep" problem: previously connections were only
 * reachable via Profile → scroll → horizontal Tabs → Connections tab.
 * This page is a direct route (`/connections`) linked from the hamburger
 * menu and the profile stat card. It mirrors the shape and handlers of
 * the existing Profile connections tab so behaviour is identical.
 *
 * Deliberately excludes the "Hidden Users" section that lives on Profile
 * — that's a settings-adjacent surface and belongs there. Users can
 * still manage hidden users from Profile → Connections.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Clock, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { API, useAuth } from '@/App';

export default function MyConnections() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState({
    contacts: [],
    pending_requests: [],
    sent_requests: [],
  });

  // Preselect the "pending" tab when we deep-linked here specifically to
  // triage requests (e.g. from a push notification).
  const initialTab = new URLSearchParams(window.location.search).get('tab') === 'pending'
    ? 'pending'
    : 'contacts';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (!user) return;
    loadConnections();
  }, [user]);

  const loadConnections = async () => {
    try {
      const response = await axios.get(`${API}/connections`);
      setConnections(response.data);
    } catch (err) {
      console.error('Failed to load connections', err);
      toast.error('Failed to load your connections');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (userId) => {
    try {
      await axios.post(`${API}/connections/accept/${userId}`);
      toast.success('Connection accepted');
      loadConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleReject = async (userId) => {
    try {
      await axios.post(`${API}/connections/reject/${userId}`);
      toast.success('Connection request rejected');
      loadConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const pendingCount = connections.pending_requests.length;
  const contactsCount = connections.contacts.length;

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="rounded-full p-2"
            data-testid="my-connections-back-btn"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold" data-testid="my-connections-title">
              My Connections
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {contactsCount} contact{contactsCount === 1 ? '' : 's'}
              {pendingCount > 0 && ` · ${pendingCount} pending request${pendingCount === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full sm:w-auto bg-muted rounded-full p-1 mb-6">
            <TabsTrigger
              value="contacts"
              className="rounded-full px-4 sm:px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold text-xs sm:text-sm"
              data-testid="my-connections-tab-contacts"
            >
              <Users className="w-4 h-4 mr-1.5" />
              Contacts
              {contactsCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-white/25 text-xs">
                  {contactsCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="pending"
              className="rounded-full px-4 sm:px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold text-xs sm:text-sm relative"
              data-testid="my-connections-tab-pending"
            >
              <Clock className="w-4 h-4 mr-1.5" />
              Pending
              {pendingCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-xs">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* CONTACTS */}
          <TabsContent value="contacts">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-border">
              {contactsCount === 0 ? (
                <div className="text-center py-10">
                  <Users className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">You haven't connected with anyone yet.</p>
                  <Button
                    onClick={() => navigate('/find-expats')}
                    className="bg-primary text-primary-foreground rounded-full"
                    data-testid="my-connections-find-expats-btn"
                  >
                    Find Expats to Connect
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {connections.contacts.map((contact) => (
                    <button
                      key={contact.user_id}
                      onClick={() => navigate(`/profile/${contact.user_id}`)}
                      className="text-left p-4 bg-background rounded-xl border-2 border-border hover:shadow-lg transition-shadow"
                      data-testid={`my-connections-contact-${contact.user_id}`}
                    >
                      <div className="flex items-center gap-3">
                        {contact.profile_picture ? (
                          <img
                            src={contact.profile_picture}
                            alt={contact.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-border"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center font-bold text-primary-foreground">
                            {contact.name?.charAt(0) || '?'}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{contact.name}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {[contact.residence_city, contact.residence_country].filter(Boolean).join(', ')}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* PENDING */}
          <TabsContent value="pending">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-border">
              {pendingCount === 0 ? (
                <div className="text-center py-10">
                  <Clock className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                  <p className="text-muted-foreground">No pending requests right now.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {connections.pending_requests.map((request) => (
                    <div
                      key={request.user_id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-background rounded-xl border border-border"
                      data-testid={`my-connections-pending-${request.user_id}`}
                    >
                      <button
                        onClick={() => navigate(`/profile/${request.user_id}`)}
                        className="flex items-center gap-3 min-w-0 text-left"
                      >
                        {request.profile_picture ? (
                          <img
                            src={request.profile_picture}
                            alt={request.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-border flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center font-bold text-primary-foreground flex-shrink-0">
                            {request.name?.charAt(0) || '?'}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{request.name}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {[request.residence_city, request.residence_country].filter(Boolean).join(', ')}
                          </p>
                        </div>
                      </button>
                      <div className="flex gap-2 ml-auto sm:ml-0 flex-shrink-0">
                        <Button
                          onClick={() => handleAccept(request.user_id)}
                          size="sm"
                          className="bg-primary text-primary-foreground rounded-full px-4"
                          data-testid={`my-connections-accept-${request.user_id}`}
                        >
                          Accept
                        </Button>
                        <Button
                          onClick={() => handleReject(request.user_id)}
                          size="sm"
                          variant="outline"
                          className="rounded-full px-4"
                          data-testid={`my-connections-reject-${request.user_id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Manage hidden users → deep-link back to profile */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Looking for hidden users? Manage them on your{' '}
          <button
            onClick={() => navigate('/profile')}
            className="underline underline-offset-2 hover:text-foreground"
            data-testid="my-connections-manage-hidden-link"
          >
            profile page
          </button>
          .
        </p>
      </div>
    </div>
  );
}
